import { cn } from '@/lib/utils';
import { Check } from 'lucide-react';
import * as React from 'react';

export interface FilterOption {
  label: string;
  value: string;
  color?: string;
  icon?: string; // URL to an image icon
  renderPrefix?: React.ReactNode;
  /** Replaces the entire label area (prefix + icon + color + text) with custom content */
  renderContent?: React.ReactNode;
  /**
   * When true and `icon` is absent, reserve the same width as a 16px icon so labels align
   * with rows that have an icon (e.g. Source filter).
   */
  alignIconSlot?: boolean;
}

interface FilterOptionListProps {
  options: FilterOption[];
  selected: string[];
  onChange: (selected: string[]) => void;
  className?: string;
  searchPlaceholder?: string;
  showSearch?: boolean;
}

export const FilterOptionList: React.FC<FilterOptionListProps> = ({
  options,
  selected,
  onChange,
  className,
  searchPlaceholder = 'Search...',
  showSearch = true,
}) => {
  const [searchInput, setSearchInput] = React.useState('');

  const toggleOption = (value: string) => {
    if (selected.includes(value)) {
      onChange(selected.filter(v => v !== value));
    } else {
      onChange([...selected, value]);
    }
  };

  // Separate selected and unselected options
  const selectedOptions = options.filter(opt => selected.includes(opt.value));
  const unselectedOptions = options.filter(
    opt => !selected.includes(opt.value)
  );

  // Filter unselected options based on search (keep all selected visible)
  const filteredUnselectedOptions = unselectedOptions.filter(opt =>
    opt.label.toLowerCase().includes(searchInput.toLowerCase())
  );

  const hasResults =
    selectedOptions.length > 0 || filteredUnselectedOptions.length > 0;

  return (
    <div className={cn('flex flex-col min-w-[200px]', className)}>
      {/* Search input */}
      {showSearch && (
        <div className="border-b">
          <input
            type="text"
            placeholder={searchPlaceholder}
            value={searchInput}
            onChange={e => setSearchInput(e.target.value)}
            className="w-full bg-transparent px-3 py-2 text-sm outline-none placeholder:text-muted-foreground"
          />
        </div>
      )}

      {/* Options list */}
      <div className="max-h-[280px] overflow-y-auto">
        <div className="py-1">
          {!hasResults && (
            <div className="text-muted-foreground py-4 text-center text-sm">
              No results found.
            </div>
          )}

          {/* Selected options first */}
          {selectedOptions.map(option => (
            <button
              key={option.value}
              onClick={() => toggleOption(option.value)}
              className={cn(
                'flex w-full items-center justify-between gap-2 px-3 py-1.5 text-sm',
                'hover:bg-accent focus-visible:outline-none focus-visible:bg-accent'
              )}
            >
              {option.renderContent ?? (
                <span className="flex items-center gap-2 min-w-0">
                  {option.renderPrefix}
                  {option.icon ? (
                    <img
                      src={option.icon}
                      alt=""
                      className="w-4 h-4 shrink-0 object-contain"
                    />
                  ) : option.alignIconSlot ? (
                    <span className="w-4 h-4 shrink-0" aria-hidden />
                  ) : null}
                  {option.color && !option.renderPrefix && (
                    <span
                      className="h-3 w-3 shrink-0 rounded-full"
                      style={{ backgroundColor: option.color }}
                    />
                  )}
                  <span className="truncate">{option.label}</span>
                </span>
              )}
              <Check className="h-4 w-4 shrink-0 text-primary" />
            </button>
          ))}

          {/* Separator if both groups have items */}
          {selectedOptions.length > 0 &&
            filteredUnselectedOptions.length > 0 && (
              <div className="my-1 border-t" />
            )}

          {/* Unselected options */}
          {filteredUnselectedOptions.map(option => (
            <button
              key={option.value}
              onClick={() => toggleOption(option.value)}
              className={cn(
                'flex w-full items-center justify-between gap-2 px-3 py-1.5 text-sm',
                'hover:bg-accent focus-visible:outline-none focus-visible:bg-accent'
              )}
            >
              {option.renderContent ?? (
                <span className="flex items-center gap-2 min-w-0">
                  {option.renderPrefix}
                  {option.icon ? (
                    <img
                      src={option.icon}
                      alt=""
                      className="w-4 h-4 shrink-0 object-contain"
                    />
                  ) : option.alignIconSlot ? (
                    <span className="w-4 h-4 shrink-0" aria-hidden />
                  ) : null}
                  {option.color && !option.renderPrefix && (
                    <span
                      className="h-3 w-3 shrink-0 rounded-full"
                      style={{ backgroundColor: option.color }}
                    />
                  )}
                  <span className="truncate">{option.label}</span>
                </span>
              )}
              {/* Empty space where checkmark would be for alignment */}
              <span className="h-4 w-4 shrink-0" />
            </button>
          ))}
        </div>
      </div>
    </div>
  );
};
